<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <h4 qid="generic-controls-page-title" :class="{'mb-1': getGuide('generic_controls_list_page')}">
      <button class="btn btn-secondary-dark float-right"
              @click="handleFilterButton(false)"
              qid="generic-controls-filter-button"
      >
        <font-awesome-icon icon="filter"/>
      </button>
      <router-link
          to="/generic-controls/create"
          class="btn btn-success float-right"
          qid="create-s-button"
          v-if="isGranted(permissions.GENERIC_CONTROL_CREATE)"
      >
          <font-awesome-icon icon="plus-circle" /> {{ $t('generic_controls.create_button') }}
      </router-link>
      <b-form-input
        type="text"
        v-model="filters.keywordValue"
        class="float-right mw-300 mr-3 search-bar"
        qid="search-s-input"
        v-on:input="debounceInput"
        :placeholder="$t('generic_controls.search_placeholder')"
      ></b-form-input>
      {{ $t('generic_controls.title') }}
    </h4>
    <div>
      <span v-if="getGuide('generic_controls_list_page')">
        <GuideButton :guide="getGuide('generic_controls_list_page')"></GuideButton>
      </span>
    </div>

    <div class="row" :class="{'table-with-filters': filterOpened}">
      <div class="col-sm-10 table-container">
        <div class="">
          <table class="table table-responsive-md table-list">
            <thead>
              <tr>
                <th scope="col"
                    class="cursor-pointer"
                    @click="selectSort('name')"
                    qid="sort-s-name"
                >
                  {{ $t('generic_controls.table.name') }}
                  <span class="ml-2">
                    <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'name'"/>
                    <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'name'"/>
                    <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'name'"/>
                  </span>
                </th>
                <th scope="col" v-if="tableColumns.responsibles">
                  {{ $t('generic_controls.table.owners') }}
                </th>
                <th scope="col" v-if="tableColumns.tags">
                  {{ $t('processes.table.tags') }}
                </th>
                <th scope="col" v-if="tableColumns.type">
                  {{ $t('generic_controls.table.control_type') }}
                </th>
                <th scope="col" v-if="tableColumns.scope">
                  {{ $t('table.scope') }}
                </th>
                <th scope="col"
                    class="cursor-pointer"
                    @click="selectSort('updated_at')"
                    qid="sort-s-name"
                >
                  {{ $t('system.last_updated') }}
                  <span class="ml-2">
                    <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'updated_at'"/>
                    <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'updated_at'"/>
                    <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'updated_at'"/>
                  </span>
                </th>
                <th width="180" class="text-right">
                  <b-nav-item-dropdown right extra-toggle-classes="no-dropdown-caret" class="guide-settings">
                    <!-- Using 'button-content' slot -->
                    <template slot="button-content">
                      <span class="float-right" style="list-style: none;">
                        <font-awesome-icon size="lg" icon="cog" class="text-muted"/>
                      </span>
                    </template>

                    <div class="pl-4 pr-4">
                      <b-form-checkbox
                        v-model="tableColumns.responsibles"
                        name="checkbox-disable"
                        :value="1"
                        :unchecked-value="0"
                      >
                        {{ $t('processes.table.owners') }}
                      </b-form-checkbox>
                      <br>
                      <b-form-checkbox
                        v-model="tableColumns.tags"
                        name="checkbox-disable"
                        :value="1"
                        :unchecked-value="0"
                      >
                        {{ $t('processes.table.tags') }}
                      </b-form-checkbox>
                      <br>
                      <b-form-checkbox
                        v-model="tableColumns.type"
                        name="checkbox-disable"
                        :value="1"
                        :unchecked-value="0"
                      >
                        {{ $t('generic_controls.table.control_type') }}
                      </b-form-checkbox>
                      <br>
                      <b-form-checkbox
                        v-model="tableColumns.scope"
                        name="checkbox-disable"
                        :value="1"
                        :unchecked-value="0"
                      >
                        {{ $t('table.scope') }}
                      </b-form-checkbox>
                      <br>
                    </div>
                    <hr>
                    <div class="pl-4 pr-4">
                      <small class="text-muted">
                        * {{ $t('system.global_table_expand_explainer_text') }}
                      </small>
                    </div>
                  </b-nav-item-dropdown>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="object in tableItems" :key="object.id">
                <td>
                  <router-link
                      :to="{ name: 'generic_controls.view', params: { slug: object.slug }}"
                      qid="s-view-page-link"
                  >
                    {{ object.name }}
                  </router-link>
                </td>
                <td v-if="tableColumns.responsibles">
                  <span class="mr-1 text-normal" v-for="user in getReducedUsers(object.users)">
                    <span qid="profile-photo">
                      <Avatar :object="user" size="xs"></Avatar>
                    </span>
                  </span>
                  <span v-if="object.users.length > 3"
                        class="badge cursor-pointer"
                        @click="openUsersModal(object)"
                  >
                    <font-awesome-icon icon="users"/>
                    + {{ object.users.length - 3 }}
                  </span>
                </td>
                <td v-if="tableColumns.tags">
                  <span v-for="tag in getReducedTags(object.tags_translated)" qid="p-tags-list">
                    <span class="badge" :title="tag.name_translated">
                      <font-awesome-icon icon="tag"/>
                      {{ tag.name_translated | truncate(35) }}
                    </span>
                  </span>
                  <span v-if="object.tags_translated.length > 3"
                        class="badge cursor-pointer"
                        @click="openTagsModal(object)"
                  >
                    <font-awesome-icon icon="tags"/>
                    + {{ object.tags_translated.length - 3 }}
                  </span>
                </td>
                <td v-if="tableColumns.type">
                  <span v-if="object.type" >
                    <span class="badge">
                      <font-awesome-icon icon="shield-alt"/>
                      {{ $t('generic_controls.category.' + $options.filters.slugify(object.type)) }}
                    </span>
                  </span>
                </td>
                <td v-if="tableColumns.scope">
                  <span v-if="object.scopes">
                    <span class="mr-1 badge" v-for="scope in object.scopes">
                      <font-awesome-icon icon="crosshairs"/>
                      {{ $t('risk_scenarios.type.' + $options.filters.slugify(scope)) }}
                    </span>
                  </span>
                </td>
                <td>
                  <span class="text-normal">
                    {{ object.updated_at | formatDate }}
                  </span>
                </td>
                <td align="right">
                  <GenericControlsEditForm
                      :object="object"
                      @reloadContent="loadContent(currentPage)"
                      v-if="isGranted([permissions.GENERIC_CONTROL_EDIT, permissions.GENERIC_CONTROL_EDIT_OWN], object) && (!getClient.parent || (getClient.parent && object.partition))"
                  ></GenericControlsEditForm>
                  <span class="btn btn-delete"
                        qid="delete-s-modal"
                        v-if="isGranted([permissions.GENERIC_CONTROL_DELETE, permissions.GENERIC_CONTROL_DELETE_OWN], object) && (!getClient.parent || (getClient.parent && object.partition))"
                        @click="showConfirmDeleteModal(object)"
                  >
                    <font-awesome-icon icon="trash-alt"/>
                  </span>
                </td>
              </tr>
              <tr v-if="!loading && !tableItems.length" qid="no-results-row" class="text-center">
                <td colspan="10">
                  <h5 class="mt-3">
                    {{ $t('system.no_results') }}
                  </h5>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="mt-3 pagination row">
          <div class="col-md-6">
            <div class="row">
              <div class="float-left">
                <span class="text-muted pagination-counter">
                  {{ $t('pagination.entries_per_page') }}:
                </span>
              </div>
              <div class="float-left">
                <multiselect
                    v-model="perPage"
                    :options="perPageOptions"
                    track-by="value"
                    label="value"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    qid="create-org-form-organization-level"
                    @select="perPageChanged"
                    :placeholder="selectedPerPage"
                    @remove="perPageRemoved"
                ></multiselect>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <b-pagination
                class="float-right"
                v-model="currentPage"
                :total-rows="rows"
                :per-page="selectedPerPage"
                aria-controls="my-table"
                @change="pageChanged"
                limit="1"
            >
              <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
              <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
              <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
              <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
              <div slot="ellipsis-text">
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
              </div>
              <span class="text-muted" slot="page" slot-scope="{ page, active }">
                <span v-if="active">{{getPerPage}}</span>
              </span>
            </b-pagination>
          </div>
        </div>
      </div>
      <div class="col-sm-2 filter-container">
        <div class="card">
          <div class="card-body">
            <b-form-group v-if="isMember()">
              <label qid="create-s-users-label">
                {{ $t('generic_controls.create.owners_label') }}
              </label>
              <multiselect
                  v-model="filters.owners"
                  @select="debounceInput"
                  @remove="debounceInput"
                  :options="users"
                  :multiple="true"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  :taggable="false"
                  track-by="slug"
                  :placeholder="$t('generic_controls.create.owners_placeholder')"
                  label="name"
                  @search-change="loadUsers"
                  qid="filter-s-form-owners"
              >
                <template slot="tag" slot-scope="props">
                  <span class="mr-3 user-tag">
                    <Avatar :object="props.option" size="xs"></Avatar>
                    <span class="ml-2">{{ props.option.name }}</span>
                  </span>
                </template>
                <template slot="option" slot-scope="props">
                  <Avatar :object="props.option" size="xs"></Avatar>
                  <span class="ml-2">
                    {{ props.option.name }}
                  </span>
                </template>
              </multiselect>
            </b-form-group>
            <b-form-group>
              <label qid="filter-p-type-label">
                {{ $t('generic_controls.create.control_type_label') }}
              </label>
              <multiselect
                  v-model="filters.category"
                  :options="categories"
                  :placeholder="$t('generic_controls.create.control_type_placeholder')"
                  @select="debounceInput"
                  @remove="debounceInput"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  track-by="value"
                  label="name"
                  qid="filter-p-form-process_group"
              ></multiselect>
            </b-form-group>
            <b-form-group>
              <label qid="filter-p-type-label">

                {{ $t('generic_controls.table.type') }}
              </label>
              <multiselect
                  v-model="filters.types"
                  :options="types"
                  :multiple="true"
                  :placeholder="$t('generic_controls.table.type')"
                  @select="debounceInput"
                  @remove="debounceInput"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  track-by="value"
                  label="name"
                  qid="filter-p-form-process_group"
              ></multiselect>
            </b-form-group>
            <b-form-group>
              <label qid="filter-p-type-label">
                {{ $t('table.scope') }}
              </label>
              <multiselect
                  v-model="filters.scopes"
                  :options="scopes"
                  :multiple="true"
                  :placeholder="$t('table.scope')"
                  @select="debounceInput"
                  @remove="debounceInput"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  track-by="value"
                  label="name"
                  qid="filter-p-form-process_group"
              ></multiselect>
            </b-form-group>
            <b-form-group>
              <label qid="create-do-tags-label">
                {{ $t('processes.create.tags_label') }}
              </label>
              <multiselect
                  v-model="filters.tags"
                  @select="debounceInput"
                  @remove="debounceInput"
                  :options="tags"
                  :multiple="true"
                  :taggable="true"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  track-by="name_translated"
                  :placeholder="$t('processes.create.tags_placeholder')"
                  label="name_translated"
                  qid="filter-p-form-tags"
                  @search-change="loadTags"
                  @tag="addTag"
              ></multiselect>
            </b-form-group>

            <b-form-group>
              <label qid="create-s-users-label">
                {{ $t('table.last_updated_by') }}
              </label>
              <multiselect
                  v-model="filters.modifiedBy"
                  @select="debounceInput"
                  @remove="debounceInput"
                  :options="users"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  :multiple="false"
                  :taggable="false"
                  track-by="slug"
                  :placeholder="$t('table.last_updated_by')"
                  label="name"
                  @search-change="loadUsers"
                  qid="filter-s-form-modified-by"
              >
                <template slot="singleLabel" slot-scope="props">
                  <Avatar :object="props.option" size="xs"></Avatar>
                  <span class="ml-2">
                    {{ props.option.name }}
                  </span>
                </template>
                <template slot="option" slot-scope="props">
                  <Avatar :object="props.option" size="xs"></Avatar>
                  <span class="ml-2">
                    {{ props.option.name }}
                  </span>
                </template>
              </multiselect>
            </b-form-group>

            <b-form-group>
              <label qid="filter-s-modified-from-label">
                {{ $t('system.modified_from') }}
              </label>
              <datepicker
                  class="date-picker"
                  qid="filter-s-form-modified-from"
                  calendar-class="right"
                  @selected="debounceInput"
                  @remove="debounceInput"
                  v-model="filters.modifiedFrom"
                  :clear-button="true"
                  :placeholder="$t('system.modified_from')"
              ></datepicker>
            </b-form-group>

            <b-form-group>
              <label qid="filter-s-modified-to-label">
                {{ $t('system.modified_to') }}
              </label>
              <datepicker
                  class="date-picker"
                  qid="filter-s-form-modified-to"
                  calendar-class="right"
                  @selected="debounceInput"
                  @remove="debounceInput"
                  v-model="filters.modifiedTo"
                  :clear-button="true"
                  :placeholder="$t('system.modified_to')"
              ></datepicker>
            </b-form-group>

            <a href="#" @click="resetFilters" class="text-right" qid="filter-s-reset">
              {{ $t('system.reset_filters') }}
            </a>
          </div>
        </div>
      </div>
    </div>


    <!-- Delete confirm modal template -->
    <b-modal ref="delete-activity" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-s-title">
          {{ $t('generic_controls.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('generic_controls.delete.description') }} {{ itemToDelete.name }}
      </h5>
      <h5 class="text-center">
        {{ $t('generic_controls.delete.task_notice') }}
      </h5>
      <h5 class="text-center">
        {{ $t('generic_controls.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideConfirmDeleteModal"
                  qid="delete-s-cancel-button"
        >
          {{ $t('generic_controls.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteS(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-s-submit-button"
                  v-if="isGranted([permissions.GENERIC_CONTROL_DELETE, permissions.GENERIC_CONTROL_DELETE_OWN], itemToDelete)"
        >
          {{ $t('generic_controls.delete.submit') }}
        </b-button>
      </div>
    </b-modal>

    <ShowMoreModal
        :items="selectedItem.users"
        :show="showUsersModal"
        @closeViewModal="closeUsersModal"
        type="users"
    ></ShowMoreModal>

    <ShowMoreModal
        :items="selectedItem.tags_translated"
        :show="showTagsModal"
        @closeViewModal="closeTagsModal"
        type="tags"
    ></ShowMoreModal>

  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import {config} from '@/config.js';
  import Avatar from '@/components/Avatar.vue';
  import ShowMoreModal from '@/components/ShowMoreModal.vue';
  import Datepicker from 'vuejs-datepicker';
  import roleCheck from '@/util/ACL/roleCheck.js'
  import GenericControlsEditForm from '@/views/GenericControls/GenericControlsEditForm.vue';
  import GuideButton from '@/components/guide/GuideButton.vue';


  export default {
    name: 'Projects',
    data: function() {
      return {
        breadcrumbItems: [
          {
            text: this.$t('dashboard.title'),
            to: '/'
          },
          {
            text: this.$t('generic_controls.title'),
            to: '/generic-controls'
          }
        ],
        tableItems: [],
        itemToDelete: {},
        selectedSort: '',
        selectedDirection: '',
        filterOpened: false,
        keywordValue: '',
        filters: {
          projectScope: [],
          projectType: [],
          owners: [],
          modifiedBy: [],
          modifiedFrom: '',
          modifiedTo: '',
          tags:[],
          types: [],
          category: [],
          keywordValue: '',
          scopes: [],
        },
        tableColumns: {
          responsibles: 1,
          tags: 1,
          type: 1,
          control_type: 1,
          scope: 1,
        },
        scopes: [
          { name: this.$t('risk_scenarios.type.process'), value: 'process' },
          { name: this.$t('risk_scenarios.type.activity'), value: 'activity' },
          { name: this.$t('risk_scenarios.type.system'), value: 'system' },
          { name: this.$t('risk_scenarios.type.third_party'), value: 'third_party' }
        ],
        types: [
          { name: this.$t('generic_controls.control_type.preventive'), value: 'preventive' },
          { name: this.$t('generic_controls.control_type.detective'), value: 'detective' },
          { name: this.$t('generic_controls.control_type.corrective'), value: 'corrective' }
        ],
        categories: [
          { name: this.$t('generic_controls.category.control'), value: 'Control' },
          { name: this.$t('generic_controls.category.security_measure'), value: 'Security measure' }
        ],
        typeInput: '',
        loading: true,

        // Pagination
        perPageOptions: [
          { name: '10', value: '10' },
          { name: '20', value: '20' },
          { name: '50', value: '50' },
          { name: '100', value: '100' }
        ],
        perPage: '',
        selectedPerPage: "10",
        currentPage: 1,
        totalRows: 0,
        users: [],
        selectedUsers: [],

        selectedItem: {},
        showUsersModal: false,
        showTagsModal: false,
        tags: [],
        newTags: [],
        myGuides: []
      }
    },
    components: {
      Avatar,
      Datepicker,
      ShowMoreModal,
      GenericControlsEditForm,
      GuideButton
    },
    computed: {
      rows() {
        // Use computed value for rows because of dynamic update
        return this.totalRows
      },
      getClient() {
        return this.$store.state.client
      },
      getFilters() {
        return this.$store.state.activeFilters
      },
      getTableColumns() {
        if (localStorage.getItem('tableColumns')) {
          return JSON.parse(localStorage.getItem('tableColumns'))
        } else {
          return {}
        }
      },
      getStorePerPage() {
        return this.$store.state.perPage
      },
      getPerPage() {
        let page = this.currentPage;
        let selectedFrom = (page-1) * Number(this.selectedPerPage) + 1;
        let selectedTo = (page-1) * Number(this.selectedPerPage) + Number(this.selectedPerPage);
        let selectedOf = this.totalRows;

        if(selectedTo > selectedOf){
          selectedTo = selectedOf
        }

        return selectedFrom + " - " + selectedTo + " " + this.$t('system.of') + " " + selectedOf
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {
        this.loadContent()
        if (this.getTableColumns.generic_controls) {
          this.tableColumns = this.getTableColumns.generic_controls
        }
      }
    },
    methods: {
      addTag(newTag) {
        const tag = {
          name: newTag,
          translatable: newTag,
          name_translated: newTag
        }
        this.selectedTags.push(tag)
        this.newTags.push(tag)
      },
      debounceInput: _.debounce(function() {
        this.loadContent()
      }, 400),
      goToHome() {
        this.$router.push('/')
      },
      resetFilters() {
        this.filters.owners = [];
        this.filters.modifiedBy = [];
        this.filters.modifiedFrom = '';
        this.filters.modifiedTo = '';
        this.filters.types = [];
        this.filters.category = [];
        this.filters.tags = [];
        this.filters.scopes = [];
        this.debounceInput();
      },
      openTagsModal(object) {
        this.selectedItem = object
        this.showTagsModal = true
      },
      closeTagsModal() {
        this.selectedItem = {}
        this.showTagsModal = false
      },
      getReducedTags(tags) {
        let reducedTags = []
        if (tags.length > 3) {
          reducedTags = _.cloneDeep(tags);
          reducedTags = reducedTags.slice(0, 3);
        } else {
          reducedTags = tags;
        }
        return reducedTags;
      },
      loadTags(query) {
        let self = this;
        piincHttp.get('tags', { params:
                  {
                    client: this.getClient.slug,
                    keyword: query ? query : undefined
                  }
        }).then(function(response) {
          self.tags = response.data
          for (var i = 0; i < self.newTags.length; i++) {
            self.tags.push(self.newTags[i]);
          }
          if (self.tags && !self.tags.length && !query) {
            self.tags.push({name_translated: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
      handleFilterButton(forceOpened) {
        if (!this.filterOpened) {
          this.loadTags()
          this.loadUsers()
        }
        if (forceOpened) {
          this.filterOpened = true
        } else {
          this.filterOpened = !this.filterOpened
        }
      },
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Generic control' && object.enabled
        });
        return existingGuide
      },
      openUsersModal(object) {
        this.selectedItem = object
        this.showUsersModal = true
      },
      closeUsersModal() {
        this.selectedItem = {}
        this.showUsersModal = false
      },
      getReducedUsers(users) {
        let reducedUsers = []
        if (users && users.length > 3) {
          reducedUsers = _.cloneDeep(users);
          reducedUsers = reducedUsers.slice(0, 3);
        } else {
          reducedUsers = users;
        }
        return reducedUsers;
      },
      isMember() {
        if (this.getClient && this.getClient.user_roles && this.getClient.user_roles.length == 1 && this.getClient.user_roles[0].name == 'Member') {
          return false;
        } else {
          return true;
        }
      },
      selectSort(sort) {
        // If old and new sort are same, switch just direction
        if (sort === this.selectedSort) {
          this.selectedDirection = this.selectedDirection === 'desc' ? 'asc' : 'desc';
        } else {
          // If different sort, set sort and set direction to default
          this.selectedDirection = 'desc'
          this.selectedSort = sort;
        }
        this.loadContent();
      },
      handleTypeInput(input) {
        this.typeInput = input;
      },
      loadContent(page, perPage) {
        if (this.getStorePerPage) {
          this.selectedPerPage = this.getStorePerPage;
        }

        // If page is not defined, set page to default 1
        let pageNumber = page ? page : 1;
        let perPageNumber = perPage ? perPage : this.selectedPerPage;
        let self = this;
        let formOwners = [];
        let formTypes = [];
        let formCategory = [];
        let formTags = [];
        let formScopes = [];

        // Append list of users to FormData
        if (this.filters.owners && this.filters.owners.length) {
          for (var i = 0; i < this.filters.owners.length; i++) {
            formOwners[i] = this.filters.owners[i].id;
          }
          this.handleFilterButton(true)
        }

        if (this.filters.types && this.filters.types.length) {
          for (var t = 0; t < this.filters.types.length; t++) {
            formTypes[t] = this.filters.types[t].value;
          }
          this.handleFilterButton(true)
        }


        if (this.filters.scopes && this.filters.scopes.length) {
          for (var s = 0; s < this.filters.scopes.length; s++) {
            formScopes[s] = this.filters.scopes[s].value;
          }
          this.handleFilterButton(true)
        }

        if (
          (this.filters.modifiedBy && this.filters.modifiedBy.id) ||
          (this.filters.category && this.filters.category.value) ||
          this.filters.modifiedFrom ||
          this.filters.modifiedTo
        ) {
          this.handleFilterButton(true)
        }

        if (this.filters.tags && this.filters.tags.length) {
          for (var t = 0; t < this.filters.tags.length; t++) {
            formTags[t] = this.filters.tags[t].id;
          }
          this.handleFilterButton(true)
        }


        piincHttp.get('generic-controls', { params:
          {
            page: pageNumber,
            per_page: perPageNumber,
            sort: this.selectedSort !== '' ? this.selectedSort : undefined,
            order: this.selectedDirection !== '' ? this.selectedDirection : undefined,
            owners: formOwners ? formOwners : undefined,
            control_types: formTypes ? formTypes : undefined,
            category: this.filters.category ? this.filters.category.value : undefined,
            modified_by: this.filters.modifiedBy ? this.filters.modifiedBy.id : undefined,
            modified_from: this.filters.modifiedFrom ? this.filters.modifiedFrom : undefined,
            modified_to: this.filters.modifiedTo ? this.filters.modifiedTo : undefined,
            keyword: this.filters.keywordValue !== '' ? this.filters.keywordValue : undefined,
            client: this.getClient.slug,
            tags: formTags ? formTags : undefined,
            scopes: formScopes ? formScopes : undefined,
          }
        }).then(function(response) {
          self.loading = false;
          self.tableItems = response.data.items;
          // Set number of total items
          self.totalRows = response.data.total_count;
        }, function() {});
      },
      pageChanged(page) {
        // Load Third parties when page changed listener is triggered
        this.loadContent(page)
      },
      perPageChanged(perPage) {
        let self = this;
        // Load Third parties when page changed listener is triggered
        this.loadContent(1, perPage.value)
        self.selectedPerPage = perPage.value
        this.$store.commit('storePerPage', this.selectedPerPage);
      },
      perPageRemoved() {
        let self = this;
        // Load Third parties when page changed listener is triggered
        self.selectedPerPage = self.perPageOptions[0].value
        this.loadContent(1, self.selectedPerPage)

      },
      showConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-activity'].show()
      },
      hideConfirmDeleteModal() {
        this.itemToDelete = {}
        this.$refs['delete-activity'].hide()
      },
      confirmDeleteS(object) {
        let self = this;
        piincHttp.delete('generic-controls/' + object.slug).then(function(response) {
          self.hideConfirmDeleteModal();
          self.loadContent(self.currentPage);
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('generic_controls.delete.success'))
        }, function() {});
      },
      loadUsers(query) {
        let self = this;
        piincHttp.get('users', { params:
          {
            client: this.getClient.id,
            keyword: query ? query : undefined,
            per_page: 50
          }
        }).then(function(response) {
          self.users = response.data.items
          if (self.users && !self.users.length && !query) {
            self.users.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Generic control',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
          }
        }, function() {});
      }
    },
    loadTags(query) {
      let self = this;
      piincHttp.get('tags', { params:
                {
                  client: this.getClient.slug,
                  keyword: query ? query : undefined
                }
      }).then(function(response) {
        self.tags = response.data
        for (var i = 0; i < self.newTags.length; i++) {
          self.tags.push(self.newTags[i]);
        }
        if (self.tags && !self.tags.length && !query) {
          self.tags.push({name_translated: self.$t('system.no_results'), $isDisabled: true})
        }
      }, function() {});
    },
    beforeRouteLeave(to, from, next) {
      let object = {
        type: 'generic_controls',
        filters: this.filters
      }
      let columnsObject = {
        type: 'generic_controls',
        tableColumns: this.tableColumns
      }
      this.$store.commit('storeTableColumns', columnsObject);
      this.$store.commit('storeActiveFilters', object);
      this.$store.commit('storePerPage', this.selectedPerPage);
      next()
    },
    created () {
      if (this.getClient && this.getClient.slug) {
        if (this.getFilters.generic_controls) {
          this.filters = this.getFilters.generic_controls
        }
        if (this.getTableColumns.generic_controls) {
          this.tableColumns = this.getTableColumns.generic_controls
        }
        this.loadContent()
      }
      this.loadGuides('generic_controls_list_page')
    }
  }
</script>
